/**
 * Auth Service
 *
 * API service for authentication operations.
 * Handles login, signup, token refresh, and password reset.
 *
 * Tokens are stored in HttpOnly cookies (set by the backend).
 * Only user/household metadata is stored in localStorage for state initialization.
 */

import { fetchApi } from '@/shared/services/api';
import type {
  AuthTokenResponse,
  ForgotPasswordRequest,
  InvitedSignupRequest,
  LoginRequest,
  MessageResponse,
  ResetPasswordRequest,
  SignupRequest,
  VerifyEmailRequest,
  InvitationCreate,
  InvitationListItem,
  InvitationValidation,
} from '../types';

// =============================================================================
// Auth Data Storage (user/household only — tokens are in HttpOnly cookies)
// =============================================================================

const STORAGE_KEYS = {
  user: 'authUser',
  household: 'authHousehold',
} as const;

export function storeAuthData(response: AuthTokenResponse) {
  localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(response.user));
  localStorage.setItem(STORAGE_KEYS.household, JSON.stringify(response.household));
}

export function clearStoredAuth() {
  localStorage.removeItem(STORAGE_KEYS.user);
  localStorage.removeItem(STORAGE_KEYS.household);
  // Also clean up legacy token keys if present
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
}

export function getStoredAuthData() {
  const userJson = localStorage.getItem(STORAGE_KEYS.user);
  const householdJson = localStorage.getItem(STORAGE_KEYS.household);

  if (!userJson || !householdJson) {
    return null;
  }

  try {
    return {
      user: JSON.parse(userJson),
      household: JSON.parse(householdJson),
    };
  } catch {
    return null;
  }
}

// =============================================================================
// Auth API
// =============================================================================

export const authService = {
  /**
   * Register a new user and create a household.
   * Returns a message to check email for verification.
   */
  signup: (request: SignupRequest) =>
    fetchApi<MessageResponse>('/auth/signup', {
      method: 'POST',
      body: request,
    }),

  /**
   * Register using an invitation token.
   * Returns user/household (tokens set via cookies).
   */
  signupInvited: (request: InvitedSignupRequest) =>
    fetchApi<AuthTokenResponse>('/auth/signup/invited', {
      method: 'POST',
      body: request,
    }),

  /**
   * Verify email and get auth (tokens set via cookies).
   */
  verifyEmail: (request: VerifyEmailRequest) =>
    fetchApi<AuthTokenResponse>('/auth/verify-email', {
      method: 'POST',
      body: request,
    }),

  /**
   * Login with email and password (tokens set via cookies).
   */
  login: (request: LoginRequest) =>
    fetchApi<AuthTokenResponse>('/auth/login', {
      method: 'POST',
      body: request,
    }),

  /**
   * Request a password reset email.
   */
  forgotPassword: (request: ForgotPasswordRequest) =>
    fetchApi<MessageResponse>('/auth/forgot-password', {
      method: 'POST',
      body: request,
    }),

  /**
   * Reset password using token.
   */
  resetPassword: (request: ResetPasswordRequest) =>
    fetchApi<MessageResponse>('/auth/reset-password', {
      method: 'POST',
      body: request,
    }),

  /**
   * Refresh the access token (cookie-based, no body needed).
   */
  refresh: () =>
    fetchApi<AuthTokenResponse>('/auth/refresh', {
      method: 'POST',
      skipAuth: true,
    }),

  /**
   * Logout and revoke refresh token (cookie-based, no body needed).
   */
  logout: () =>
    fetchApi<MessageResponse>('/auth/logout', {
      method: 'POST',
      skipAuth: true,
    }),

  /**
   * Logout from all devices (cookie-based, no body needed).
   */
  logoutAll: () =>
    fetchApi<MessageResponse>('/auth/logout-all', {
      method: 'POST',
    }),

  /**
   * Permanently delete the current user's account (requires password confirmation).
   */
  deleteMyAccount: (password: string) =>
    fetchApi<{ message: string; summary: Record<string, unknown> }>('/users/me', {
      method: 'DELETE',
      body: { password },
    }),
};

// =============================================================================
// Invitation API
// =============================================================================

export const invitationService = {
  /**
   * List pending invitations (owner only).
   */
  list: () => fetchApi<InvitationListItem[]>('/invitations'),

  /**
   * Create a new invitation (owner only).
   */
  create: (request: InvitationCreate) =>
    fetchApi<InvitationListItem>('/invitations', {
      method: 'POST',
      body: request,
    }),

  /**
   * Revoke an invitation (owner only).
   */
  revoke: (invitationId: string) =>
    fetchApi<MessageResponse>(`/invitations/${invitationId}`, {
      method: 'DELETE',
    }),

  /**
   * Validate an invitation token (public).
   */
  validate: (token: string) =>
    fetchApi<InvitationValidation>(`/invitations/validate/${token}`),
};
